/*
 * Convert Image to Chart — state machine behind the in-pane sheet.
 *
 * Two round trips, not one, and the sheet's shape follows from that:
 *
 *   opening the sheet  → extract   (vision pass; the sheet shows a spinner)
 *   "Create Chart"     → [re-extract with the typed hint] → insert
 *
 * The first extraction runs as soon as the sheet opens, because the tile grid
 * is a *review* of what the model read — the banner in the approved design
 * says "<type> pre-selected … Review, then create", which only means anything
 * once the data is in. The second call is made only when the user actually
 * typed a description: the hint changes how the image is read ("values are in
 * £m"), so it has to reach the model, but it reuses the blob already uploaded
 * rather than re-capturing and re-sending the same pixels.
 *
 * Cancellation and mount-safety come from `useAbortableRun`, the same scaffold
 * Sketch to Slide and Photo Headshot use.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useOptionalAppConfig } from "../../../../../services/studio/AppConfigProvider";
import { useDeckorateHttp } from "../../../hooks/useDeckorateHttp";
import { runAbortable, useAbortableRun } from "../../shared/hooks/useAbortableRun";
import { getErrorMessage } from "../../shared/services/connectorErrors";
import { devTrace } from "../../shared/services/devTrace";
import {
  extractChartFromSelection,
  reExtractChartWithHint,
  type ChartExtraction,
} from "../services/chartExtraction";
import {
  CONVERT_CHART_EMPTY_MESSAGE,
  CONVERT_CHART_FAILED_MESSAGE,
  CONVERT_CHART_REQUEST_TIMEOUT_MS,
  CONVERT_CHART_TRACE_SCOPE,
  MAX_CHART_HINT_CHARS,
  describeConvertChartFailure,
  hasPlottableData,
  logConvertChartError,
  type ConvertChartResult,
  type EditableChart,
} from "../services/convertChartApi";
import {
  applyChartTypeOption,
  chartTypeOptionFor,
  chartTypeOptionOrDefault,
  DEFAULT_CHART_TYPE_OPTION_ID,
  type ChartTypeOption,
  type ChartTypeOptionId,
} from "../services/chartTypeOptions";
import { insertChartFromExtraction, type ChartInsertSummary } from "../services/chartSlideInsert";

/** Shown while the deck is being written — the insert itself is not an API
 *  call, so it never trips the request timeout and needs its own copy. */
export const CHART_CREATING_LABEL = "Creating chart…";
/** Shown while the first (or hinted) vision pass is in flight. */
export const CHART_READING_LABEL = "Reading the image…";

export type ConvertChartState =
  /** Sheet closed, or reset after a completed run. */
  | { readonly status: "idle" }
  /** A vision pass is in flight — the sheet shows its loading state. */
  | { readonly status: "extracting" }
  /** Extraction succeeded; the grid and the preview are live. */
  | { readonly status: "ready"; readonly result: ConvertChartResult }
  /** Re-reading with a hint, or building and inserting the chart. */
  | { readonly status: "creating"; readonly result: ConvertChartResult }
  | { readonly status: "done"; readonly summary: ChartInsertSummary }
  /** The user stopped the run. Not a failure, so it is not styled as one. */
  | { readonly status: "cancelled" }
  | { readonly status: "error"; readonly message: string };

export interface ConvertImageToChartController {
  readonly state: ConvertChartState;
  /** The tile currently selected — pre-set from the extraction, then owned by
   *  the user. Meaningful in every state so the grid never loses its highlight
   *  while a create is running. */
  readonly selectedOption: ChartTypeOption;
  readonly description: string;
  /** The extracted chart re-shaped to the selected tile — what the preview
   *  draws and what the insert builds. `null` before the first extraction. */
  readonly chart: EditableChart | null;
  /** Extraction caveats from the API (estimated values, dropped series, …). */
  readonly warnings: readonly string[];
  readonly selectOption: (id: ChartTypeOptionId) => void;
  readonly setDescription: (value: string) => void;
  /** Runs (or re-runs) the extraction. Also the Retry action on an error. */
  readonly extract: () => void;
  readonly createChart: () => void;
  readonly cancel: () => void;
}

/** True while a request or the deck write is in flight — the sheet disables
 *  its controls and shows a progress row against this. */
export function isConvertChartBusy(state: ConvertChartState): boolean {
  return state.status === "extracting" || state.status === "creating";
}

/** The result held by whichever state carries one, else `null`. */
function resultOf(state: ConvertChartState): ConvertChartResult | null {
  return state.status === "ready" || state.status === "creating" ? state.result : null;
}

/**
 * Binds the extraction chain to this session's Deckorate HTTP client and
 * Studio client id.
 *
 * The blob the chain uploads to is tenant-scoped, so a missing client id is
 * not something to guess around — it would either fail the blob-sas request
 * or, worse, write into another tenant's container. The sheet reports it as
 * an error rather than running (mirrors `useImageClassifier`'s reasoning,
 * which can degrade silently because classification is only an enhancement;
 * this cannot, because the user asked for a chart).
 */
export const CHART_WORKSPACE_UNAVAILABLE_MESSAGE =
  "Your workspace is still loading. Try Convert to chart again in a moment.";

export function useConvertImageToChart(open: boolean): ConvertImageToChartController {
  const http = useDeckorateHttp();
  const clientId = useOptionalAppConfig()?.data?.client.id;

  const { state, safeSetState, isRunningRef, abortRef, timedOutRef, cancel } =
    useAbortableRun<ConvertChartState>({ status: "idle" });

  const [selectedOptionId, setSelectedOptionId] = useState<ChartTypeOptionId>(DEFAULT_CHART_TYPE_OPTION_ID);
  const [description, setDescription] = useState("");

  /** The last successful extraction, kept outside state so "Create Chart" can
   *  reuse its blob without threading it through every state variant. */
  const extractionRef = useRef<ChartExtraction | null>(null);
  /** The hint the held extraction was produced with, so a create only re-reads
   *  the image when the user has actually changed the description since. */
  const appliedHintRef = useRef("");
  /** Latest selection, for callbacks that must not re-create on every pick. */
  const selectedOptionIdRef = useRef<ChartTypeOptionId>(DEFAULT_CHART_TYPE_OPTION_ID);
  /** Whether the sheet is still open, read by callbacks that resolve later. */
  const openRef = useRef(false);
  /** Set when an extraction was asked for while an older one was still
   *  unwinding; the older run's outcome handler starts it. See `extract`. */
  const pendingExtractRef = useRef(false);
  /** The current `extract`, so a run finishing later can start the queued one
   *  without capturing the identity it had when it started. Assigned below,
   *  once `extract` exists. */
  const extractRef = useRef<() => void>(() => undefined);

  const setSelection = useCallback((id: ChartTypeOptionId) => {
    selectedOptionIdRef.current = id;
    setSelectedOptionId(id);
  }, []);

  /** Public setter: caps at the API's hint length so a long steer can't 422. */
  const updateDescription = useCallback((value: string) => {
    setDescription(value.slice(0, MAX_CHART_HINT_CHARS));
  }, []);

  /**
   * Store an extraction, pre-selecting the tile it implies.
   *
   * `preselect` is false for the hinted re-read inside a create: by then the
   * user has already chosen a tile and is watching the sheet, so moving the
   * highlight under them would misreport what is about to be inserted.
   */
  const acceptExtraction = useCallback(
    (extraction: ChartExtraction, hint: string, preselect: boolean) => {
      extractionRef.current = extraction;
      appliedHintRef.current = hint;
      if (preselect) setSelection(chartTypeOptionFor(extraction.result.chart).id);
    },
    [setSelection],
  );

  const extract = useCallback(() => {
    if (isRunningRef.current) {
      // A previous run is still unwinding — an aborted one has to reject and
      // clear `useAbortableRun`'s guard before the next can take it, and that
      // happens a microtask later than this call. Two things get here that
      // way: React StrictMode remounting the sheet (effect → cleanup → effect,
      // all in one commit) and a user closing and immediately reopening it.
      // Dropping the request would leave the sheet showing the stale run's
      // "stopped" state with nothing in flight, so it is queued instead.
      pendingExtractRef.current = true;
      abortRef.current?.abort();
      return;
    }
    if (typeof clientId !== "number") {
      devTrace(CONVERT_CHART_TRACE_SCOPE, "extraction skipped: no Studio client id yet", { clientId: clientId ?? null });
      safeSetState({ status: "error", message: CHART_WORKSPACE_UNAVAILABLE_MESSAGE });
      return;
    }

    safeSetState({ status: "extracting" });
    const hint = description.trim();

    void runAbortable({ isRunningRef, abortRef, timedOutRef }, CONVERT_CHART_REQUEST_TIMEOUT_MS, (signal) =>
      extractChartFromSelection({ http, clientId, hint, options: { signal } }),
    ).then((outcome) => {
      // A queued request supersedes this run's outcome entirely: whatever this
      // one ended as describes an image the user has already moved past.
      if (pendingExtractRef.current) {
        pendingExtractRef.current = false;
        if (openRef.current) extractRef.current();
        return;
      }

      switch (outcome.kind) {
        case "skipped":
          return;
        case "cancelled":
          safeSetState({ status: "cancelled" });
          return;
        case "failed":
          logConvertChartError("convert-chart-extract", getErrorMessage(outcome.error, CONVERT_CHART_FAILED_MESSAGE));
          safeSetState({
            status: "error",
            message: describeConvertChartFailure(outcome.error, CONVERT_CHART_FAILED_MESSAGE, {
              timedOut: outcome.timedOut,
            }),
          });
          return;
        case "ok":
          // A 200 carrying no plottable point is not an HTTP failure, but there
          // is nothing to draw or insert — so it lands in the error state with
          // copy that says which of the two happened.
          if (!hasPlottableData(outcome.value.result.chart)) {
            safeSetState({ status: "error", message: CONVERT_CHART_EMPTY_MESSAGE });
            return;
          }
          acceptExtraction(outcome.value, hint, true);
          safeSetState({ status: "ready", result: outcome.value.result });
      }
    });
  }, [http, clientId, description, acceptExtraction, safeSetState, isRunningRef, abortRef, timedOutRef]);

  const createChart = useCallback(() => {
    if (isRunningRef.current) return;
    const held = extractionRef.current;
    const current = resultOf(state);
    if (!held || !current) return;

    safeSetState({ status: "creating", result: current });
    const hint = description.trim();
    const option = chartTypeOptionOrDefault(selectedOptionIdRef.current);

    void runAbortable(
      { isRunningRef, abortRef, timedOutRef },
      CONVERT_CHART_REQUEST_TIMEOUT_MS,
      async (signal) => {
        // Only re-read the image when the description has actually changed —
        // otherwise this is a second vision call that can only return what the
        // sheet is already showing.
        const extraction =
          hint === appliedHintRef.current
            ? held
            : await reExtractChartWithHint(http, held, hint || null, { signal });
        if (extraction !== held) {
          if (!hasPlottableData(extraction.result.chart)) throw new Error(CONVERT_CHART_EMPTY_MESSAGE);
          acceptExtraction(extraction, hint, false);
        }
        return insertChartFromExtraction(applyChartTypeOption(extraction.result.chart, option), option, signal);
      },
    ).then((outcome) => {
      switch (outcome.kind) {
        case "skipped":
          return;
        case "cancelled":
          // The extraction survives a cancelled create, so the sheet drops back
          // to the reviewable state rather than making the user re-read the image.
          safeSetState({ status: "ready", result: current });
          return;
        case "failed":
          logConvertChartError("convert-chart-create", getErrorMessage(outcome.error, CONVERT_CHART_FAILED_MESSAGE));
          safeSetState({
            status: "error",
            message: describeConvertChartFailure(outcome.error, CONVERT_CHART_FAILED_MESSAGE, {
              timedOut: outcome.timedOut,
            }),
          });
          return;
        case "ok":
          safeSetState({ status: "done", summary: outcome.value });
      }
    });
  }, [http, state, description, acceptExtraction, safeSetState, isRunningRef, abortRef, timedOutRef]);

  extractRef.current = extract;

  /**
   * Opening the sheet starts an extraction; closing it aborts whatever is in
   * flight and clears the held result, so re-opening on a different picture
   * can't show the previous one's data.
   *
   * `extract` is deliberately NOT a dependency, and is reached through
   * `extractRef` instead: it changes identity whenever the description does,
   * and re-running the vision call on every keystroke is exactly what this
   * effect must not do.
   */
  useEffect(() => {
    if (!open) return;
    openRef.current = true;
    extractRef.current();

    return () => {
      openRef.current = false;
      pendingExtractRef.current = false;
      abortRef.current?.abort();
      extractionRef.current = null;
      appliedHintRef.current = "";
      setDescription("");
      setSelection(DEFAULT_CHART_TYPE_OPTION_ID);
      safeSetState({ status: "idle" });
    };
  }, [open, abortRef, safeSetState, setSelection]);

  const selectedOption = chartTypeOptionOrDefault(selectedOptionId);
  // Read from state, not from `extractionRef`: a ref mutation doesn't re-render,
  // so deriving the preview's chart from one would risk drawing a stale pick.
  const current = resultOf(state);

  // Memoised because typing in the description re-renders the sheet on every
  // keystroke, and a fresh object here would rebuild the preview's geometry and
  // re-render its whole SVG each time.
  const chart = useMemo(
    () => (current ? applyChartTypeOption(current.chart, selectedOption) : null),
    [current, selectedOption],
  );

  return {
    state,
    selectedOption,
    description,
    chart,
    warnings: current?.warnings ?? [],
    selectOption: setSelection,
    setDescription: updateDescription,
    extract,
    createChart,
    cancel,
  };
}
